import { effect, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfigService } from '@rxap/config';
import { ConfirmDialogService } from '@rxap/dialog';
import { ToggleSubject } from '@rxap/rxjs';
import { lastValueFrom } from 'rxjs';
import { NetworkService } from '../network/network.service';
import { iirdsStore } from '../stores/iirds.store';
import { FlexsearchService } from './flexsearch.service';
import { SearchMode, SearchParams, SearchResponse } from './search-types';
import { ISearchService } from './search.store';
import { TypesenseService } from './typesense.service';

@Injectable({
  providedIn: 'root',
})
export class SearchService implements ISearchService {
  readonly typesense = inject(TypesenseService);
  readonly flexsearch = inject(FlexsearchService);
  readonly networkService = inject(NetworkService);
  readonly confirmDialog = inject(ConfirmDialogService);

  readonly iirds = inject(iirdsStore);

  readonly snackBar = inject(MatSnackBar);

  readonly ready$ = new ToggleSubject(false);

  readonly isReady = toSignal(this.ready$, { initialValue: false });

  readonly hasError = signal(false);

  readonly config = inject(ConfigService);

  mode = signal<SearchMode | null>(null);

  constructor() {
    effect(() => {
      // Re-check readiness whenever the network status changes
      if (this.networkService.isOnline()) {
        this.checkReady();
      } else {
        this.ready$.disable();
        if (this.mode() === SearchMode.Typesense) {
          this.checkReady(); // Trigger fallback logic
        }
      }
    });

    // Periodically check health if search is enabled
    setInterval(() => {
      this.checkReady();
    }, 1000 * 60); // Check every minute
  }

  async checkReady() {
    console.log('checkReady');
    if (!this.config.get('search.enabled', false)) {
      this.ready$.disable();
      this.hasError.set(true);
      return;
    }

    const typesenseEnabled = this.config.get('search.typesense.enabled', false);
    const flexsearchEnabled = this.config.get('search.flexsearch.enabled', false);

    if (typesenseEnabled && this.networkService.isOnline()) {
      if (await this.typesense.isHealthy()) {
        this.ready$.enable();
        if (this.mode() !== SearchMode.Typesense) {
          console.log('Typesense server is ready, switching to Typesense');
          this.mode.set(SearchMode.Typesense);
        }
        return;
      }
    }

    if (flexsearchEnabled) {
      if (this.mode() === SearchMode.Typesense) {
        // We were in Typesense mode but it's no longer healthy/online
        this.ready$.disable();
      }

      this.mode.set(SearchMode.Flexsearch);

      // Auto-restore a persisted index so search is ready after a reload
      // without rebuilding. `loadIndex()` takes the fast OPFS restore path when
      // a stored index exists; we only enter it when nothing is in memory yet.
      if (!this.flexsearch.hasIndex() && (await this.flexsearch.hasStoredIndex())) {
        try {
          const restore$ = await this.flexsearch.loadIndex();
          if (restore$) {
            await lastValueFrom(restore$);
          }
        } catch (error) {
          console.error('Failed to restore persisted flexsearch index', error);
        }
      }

      if (this.flexsearch.hasIndex()) {
        this.ready$.enable();
        console.log('Flexsearch index is ready');
      } else {
        this.ready$.disable();
      }
      return;
    }

    this.ready$.disable();
    this.hasError.set(true);
  }

  async run(params: SearchParams) {
    try {
      if (
        this.mode() === SearchMode.Typesense &&
        (!this.networkService.isOnline() || !(await this.typesense.isHealthy()))
      ) {
        if (this.config.get('search.flexsearch.enabled', false)) {
          const confirmed = await this.confirmDialog.open(
            $localize`The remote search service is currently unavailable. Would you like to switch to local search?`,
            $localize`Search Unavailable`
          );
          if (confirmed) {
            this.mode.set(SearchMode.Flexsearch);
            if (!this.flexsearch.hasIndex()) {
              await this.flexsearch.loadIndex();
            }
          } else {
            return null;
          }
        } else {
          this.snackBar.open($localize`Search is unavailable`, $localize`Close`, {
            duration: 5000,
          });
          return null;
        }
      }

      let response: SearchResponse | null = null;
      switch (this.mode()) {
        case SearchMode.Flexsearch:
          response = await this.flexsearch.search(params);
          break;
        case SearchMode.Typesense:
          response = await this.typesense.search(params);
          break;
      }

      if (!response || response.groupedHits.length === 0) {
        this.snackBar.open($localize`No results available`, $localize`Close`, {
          duration: 5000,
        });
      }
      return response;
    } catch (error: any) {
      console.error(`Search failed: ${error.message}`);
      this.snackBar.open('Search failed', 'Close', {
        duration: 5000,
      });
    }
    return null;
  }

  async loadMoreGroupHits(
    iri: string,
    pageIndex: number,
    pageSize: number,
    params: SearchParams
  ) {
    console.log('loadMoreGroupHits', iri, pageIndex, pageSize);
    try {
      switch (this.mode()) {
        case SearchMode.Flexsearch:
          break;

        case SearchMode.Typesense:
          // eslint-disable-next-line no-case-declarations
          const hits = await this.typesense.more(
            params,
            iri,
            pageIndex,
            pageSize
          );
          if (!hits || hits.length === 0) {
            this.snackBar.open('No more results available', 'Close', {
              duration: 5000,
            });
          } else {
            return hits;
          }
          break;
      }
    } catch (error: any) {
      console.error(`Load more failed: ${error.message}`);
      this.snackBar.open('Failed to load more results', 'Close', {
        duration: 5000,
      });
    }
    return null;
  }
}
